// src/features/auth/components/PasswordRequirements.tsx
import React from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface PasswordRequirementsProps {
  password: string;
  email?: string;
  className?: string;
  showTitle?: boolean;
}

interface RequirementItem {
  id: string;
  text: string;
  isMet: boolean;
  isPolicy: boolean; // true for policy requirements, false for strength feedback
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ 
  password, 
  email = '', 
  className = '',
  showTitle = true 
}) => {
  // Helper function to detect sequential characters
  const hasSequentialChars = (pwd: string): boolean => {
    // Check for 3+ consecutive ascending characters (123, abc, ABC)
    for (let i = 0; i < pwd.length - 2; i++) {
      const char1 = pwd.charCodeAt(i);
      const char2 = pwd.charCodeAt(i + 1);
      const char3 = pwd.charCodeAt(i + 2);
      
      if (char2 === char1 + 1 && char3 === char2 + 1) {
        return true;
      }
    }
    
    // Check for 3+ consecutive descending characters (321, cba, CBA)
    for (let i = 0; i < pwd.length - 2; i++) {
      const char1 = pwd.charCodeAt(i);
      const char2 = pwd.charCodeAt(i + 1);
      const char3 = pwd.charCodeAt(i + 2);
      
      if (char2 === char1 - 1 && char3 === char2 - 1) {
        return true;
      }
    }
    
    return false;
  };

  // Client-side requirements
  const requirements: RequirementItem[] = [
    {
      id: 'length',
      text: 'At least 8 characters',
      isMet: password.length >= 8,
      isPolicy: true
    },
    {
      id: 'uppercase',
      text: 'At least one uppercase letter (A-Z)',
      isMet: /[A-Z]/.test(password),
      isPolicy: true
    },
    {
      id: 'lowercase',
      text: 'At least one lowercase letter (a-z)',
      isMet: /[a-z]/.test(password),
      isPolicy: true
    },
    {
      id: 'digits',
      text: 'At least one number (0-9)',
      isMet: /\d/.test(password),
      isPolicy: true
    },
    {
      id: 'special',
      text: 'At least one special character (!@#$%^&*)',
      isMet: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
      isPolicy: true
    },
    {
      id: 'sequential',
      text: 'No sequential characters (e.g., 123, abc)',
      isMet: !hasSequentialChars(password),
      isPolicy: true
    }
  ];

  const allRequirementsMet = requirements.every(req => req.isMet);

  return (
    <div className={`space-y-3 ${className}`}>
      {showTitle && (
        <h4 className="text-sm font-medium text-theme-text-secondary dark:text-dark-theme-text-secondary">
          Password Requirements
        </h4>
      )}

      {/* Requirements list */}
      <div className="space-y-2">
        {requirements.map((requirement) => (
          <div key={requirement.id} className="flex items-start space-x-2">
            <div className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full flex items-center justify-center ${
              requirement.isMet 
                ? 'bg-green-500 text-white' 
                : 'bg-theme-border dark:bg-dark-theme-border'
            }`}>
              {requirement.isMet ? (
                <CheckIcon className="w-3 h-3" />
              ) : (
                <XMarkIcon className="w-3 h-3 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
              )}
            </div>
            <span className={`text-xs ${
              requirement.isMet 
                ? 'text-green-600 dark:text-green-400' 
                : requirement.isPolicy
                  ? 'text-theme-text-secondary dark:text-dark-theme-text-secondary'
                  : 'text-red-500 dark:text-red-400'
            }`}>
              {requirement.text}
            </span>
          </div>
        ))}
      </div>

      {/* Overall validation status */}
      {password && (
        <div className={`text-xs font-medium ${
          allRequirementsMet 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-red-500 dark:text-red-400'
        }`}>
          {allRequirementsMet ? '✓ Password meets all requirements' : '⚠ Password does not meet requirements'}
        </div>
      )}
    </div>
  );
};

export default PasswordRequirements;